import { defineStore } from 'pinia'
import { logApiError } from '~/utils/logger'

type FormAnswer = string | string[] | boolean | number | null

type DiscountDetail
  = | { type: 'rule', rule_id: string, name: string, amount: number }
    | { type: 'coupon', code: string, name: string, amount: number }

interface CartTicketItem {
  ticket_type_id: string
  ticket_type_name: string
  ticket_type: 'week' | 'day'
  price: number
  quantity: number
  max_capacity: number
  available_spots: number
  day_tickets?: {
    date: string
    quantity: number
  }[]
}

const getEffectiveQuantity = (item: CartTicketItem) => {
  if (item.ticket_type === 'day' && item.day_tickets && item.day_tickets.length > 0)
    return item.day_tickets.reduce((sum, day) => sum + (day.quantity || 0), 0)

  return item.quantity
}

export interface AvailabilityCheckItem {
  ticket_type_id: string
  ticket_type: 'week' | 'day'
  quantity: number
  day_tickets?: {
    date: string
    quantity: number
  }[]
}

// Form question type from camp forms API
// Using simplified types to avoid deep type instantiation from recursive Json type
interface CampFormQuestion {
  id: string
  questionType: string
  label: string
  placeholder?: string
  helpText?: string
  isRequired: boolean
  options?: string[]
  validationRules?: unknown
  conditionalRule?: unknown
  orderIndex: number
  content?: string
  titleSize?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  url?: string
  linkText?: string
  imageAlt?: string
}

// Form linked to a camp
interface CampForm {
  id: string
  name: string
  description?: string
  is_required: boolean
  order_index: number
  questions: CampFormQuestion[]
}

// Ticket assignment for a participant
interface TicketAssignment {
  ticket_type_id: string
  ticket_type_name: string
  ticket_type: 'week' | 'day'
  day_date?: string // For day tickets, which day
}

interface CartParticipant {
  tempId: string
  first_name: string
  last_name: string
  date_of_birth: string
  rijksregisternummer?: string // For attendee identification/deduplication
  gender?: string
  medical_info?: string
  allergies?: string
  emergency_contact_name?: string
  emergency_contact_phone?: string
  form_responses?: {
    question_id: string
    answer: FormAnswer
  }[]

  // Day-ticket attendance: the specific camp day this row is for (YYYY-MM-DD).
  // NULL/undefined means a week ticket (all camp days).
  attendance_date?: string

  // Link to a saved attendee (vzw_user_attendees.id) when picked from the saved list
  user_attendee_id?: string

  // Track which tickets this participant is assigned to
  ticket_assignments?: TicketAssignment[]

  // Flag to indicate this participant shares data with another (deduplication)
  linked_to_participant_id?: string

  // Flag to indicate participant has no Belgian RRN (foreign national)
  no_rrn?: boolean
}

interface CartState {
  camp_id: string | null
  camp_name: string | null
  camp_location: string | null
  camp_age_min: number | null
  camp_age_max: number | null
  camp_start_date: string | null
  camp_end_date: string | null
  camp_period: string | null
  camp_refund_policy: string | null
  camp_enable_invoice: boolean | null
  camp_payment_methods: string[] | null
  items: CartTicketItem[]
  participants: CartParticipant[]
  coupon_code: string | null
  discount_amount: number
  discount_details: DiscountDetail[]
  notes: string
  step: number

  // Invoice fields (optional - for company/business bookings)
  invoice_required: boolean
  invoice_company_name: string
  invoice_vat_number: string
  invoice_address: string
  invoice_country: string

  // Camp forms loaded from API
  camp_forms: CampForm[]
  camp_forms_loaded: boolean

  // Booking invite token — bypasses capacity check for sold-out camps
  invite_token: string | null

  // When true, the end user may freely choose ticket types/quantities (phase 1 is not skipped)
  invite_free_choice: boolean
}

export const useCartStore = defineStore('cart', {
  state: (): CartState => ({
    camp_id: null,
    camp_name: null,
    camp_location: null,
    camp_age_min: null,
    camp_age_max: null,
    camp_start_date: null,
    camp_end_date: null,
    camp_period: null,
    camp_refund_policy: null,
    camp_enable_invoice: null,
    camp_payment_methods: null,
    items: [],
    participants: [],
    coupon_code: null,
    discount_amount: 0,
    discount_details: [],
    notes: '',
    step: 1,
    invoice_required: false,
    invoice_company_name: '',
    invoice_vat_number: '',
    invoice_address: '',
    invoice_country: '',
    camp_forms: [],
    camp_forms_loaded: false,
    invite_token: null,
    invite_free_choice: false,
  }),

  getters: {
    isEmpty(state): boolean {
      return state.items.length === 0
    },

    totalItems(state): number {
      return state.items.reduce((sum, item) => sum + getEffectiveQuantity(item), 0)
    },

    totalQuantity(state): number {
      return state.items.reduce((sum, item) => sum + getEffectiveQuantity(item), 0)
    },

    subtotal(state): number {
      return state.items.reduce(
        (sum, item) => sum + item.price * getEffectiveQuantity(item),
        0,
      )
    },

    total(): number {
      return Math.max(0, this.subtotal - this.discount_amount)
    },

    requiredParticipants(state): number {
      return state.items.reduce((sum, item) => sum + getEffectiveQuantity(item), 0)
    },

    hasAllParticipants(): boolean {
      return this.participants.length >= this.requiredParticipants
    },

    canProceedToPayment(): boolean {
      return (
        this.items.length > 0
        && this.participants.length >= this.requiredParticipants
        && this.participants.every(
          p => p.first_name && p.last_name && (p.no_rrn || (p.rijksregisternummer && p.date_of_birth)),
        )
      )
    },

    /**
     * Check if all required form fields are filled for all participants
     */
    allFormsComplete(state): boolean {
      if (!state.camp_forms || state.camp_forms.length === 0)
        return true

      const isBaseFieldQuestion = (label?: string | null) => {
        const normalized = (label || '').toLowerCase().trim()

        return (
          normalized.includes('voornaam')
          || normalized.includes('achternaam')
          || normalized.includes('geboorte')
        )
      }

      const isConditionMet = (
        rule: {
          triggerQuestionId: string
          operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than'
          value: string
        },
        participant: CartParticipant,
      ) => {
        const triggerResponse = participant.form_responses?.find(
          r => r.question_id === rule.triggerQuestionId,
        )

        const fieldValue = triggerResponse?.answer
        const conditionValue = rule.value

        if (rule.operator === 'equals')
          return fieldValue === conditionValue
        if (rule.operator === 'not_equals')
          return fieldValue !== conditionValue
        if (rule.operator === 'contains') {
          return fieldValue !== undefined && fieldValue !== null
            && String(fieldValue).includes(String(conditionValue))
        }
        if (rule.operator === 'greater_than')
          return Number(fieldValue) > Number(conditionValue)
        if (rule.operator === 'less_than')
          return Number(fieldValue) < Number(conditionValue)

        return true
      }

      // Get unique participants (excluding linked ones)
      const uniqueParticipants = state.participants.filter(
        p => !p.linked_to_participant_id,
      )

      // Helper function to check if a question has a valid answer for a participant
      // Extracted to reduce type nesting depth
      const isQuestionAnswered = (
        question: CampFormQuestion,
        participant: CartParticipant,
      ): boolean => {
        // Skip non-input questions (titles, paragraphs, etc.)
        if (
          ['title', 'paragraph', 'link', 'image', 'separator'].includes(
            question.questionType,
          )
        ) {
          return true
        }

        if (isBaseFieldQuestion(question.label))
          return true

        const conditionalRule = question.conditionalRule as {
          triggerQuestionId: string
          operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than'
          value: string
          action: 'show' | 'hide' | 'require'
        } | null

        if (conditionalRule) {
          const conditionMet = isConditionMet(conditionalRule, participant)

          if (conditionalRule.action === 'show' && !conditionMet)
            return true
          if (conditionalRule.action === 'hide' && conditionMet)
            return true

          // Check both camelCase and snake_case for compatibility
          const baseRequired = question.isRequired
            || (question as unknown as { is_required?: boolean }).is_required

          if (conditionalRule.action === 'require' && !conditionMet && !baseRequired)
            return true
        }

        // Check both camelCase and snake_case for compatibility
        const questionIsRequired = question.isRequired
          || (question as unknown as { is_required?: boolean }).is_required

        const requiresByCondition = conditionalRule && conditionalRule.action === 'require'
          ? isConditionMet(conditionalRule, participant)
          : false

        const isRequired = questionIsRequired || requiresByCondition

        if (!isRequired)
          return true

        // Find the answer for this question
        const response = participant.form_responses?.find(
          r => r.question_id === question.id,
        )

        const answer = response?.answer

        // Check if answer has a meaningful value
        const hasValue = answer !== undefined
          && answer !== null
          && answer !== ''
          && (!Array.isArray(answer) || answer.length > 0)

        return hasValue
      }

      // Check all forms for each participant
      // Using type assertions to break deep type instantiation chain
      const participants = uniqueParticipants as CartParticipant[]
      const forms = state.camp_forms as CampForm[]
      for (let i = 0; i < participants.length; i++) {
        const participant = participants[i]!
        for (let j = 0; j < forms.length; j++) {
          const questions = forms[j]!.questions as CampFormQuestion[]
          for (let k = 0; k < questions.length; k++) {
            if (!isQuestionAnswered(questions[k]!, participant)) {
              return false
            }
          }
        }
      }

      return true
    },

    /**
     * Get unique participants (those who need to fill forms)
     * Linked participants share form data with their linked parent
     */
    uniqueParticipants(state): CartParticipant[] {
      return state.participants.filter(p => !p.linked_to_participant_id)
    },

    /**
     * Get count of unique attendees (for form filling)
     */
    uniqueParticipantCount(): number {
      return this.uniqueParticipants.length
    },

    /**
     * Check if camp has forms that need to be filled
     */
    hasCampForms(state): boolean {
      return state.camp_forms && state.camp_forms.length > 0
    },

    /**
     * Get detailed missing form fields per participant
     */
    missingFormFieldsPerParticipant(state): Array<{ participantIndex: number, participantName: string, missingFields: string[] }> {
      if (!state.camp_forms || state.camp_forms.length === 0)
        return []

      const isBaseFieldQuestion = (label?: string | null) => {
        const normalized = (label || '').toLowerCase().trim()

        return (
          normalized.includes('voornaam')
          || normalized.includes('achternaam')
          || normalized.includes('geboorte')
        )
      }

      const isConditionMet = (
        rule: {
          triggerQuestionId: string
          operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than'
          value: string
        },
        participant: CartParticipant,
      ) => {
        const triggerResponse = participant.form_responses?.find(
          r => r.question_id === rule.triggerQuestionId,
        )

        const fieldValue = triggerResponse?.answer
        const conditionValue = rule.value

        if (rule.operator === 'equals')
          return fieldValue === conditionValue
        if (rule.operator === 'not_equals')
          return fieldValue !== conditionValue
        if (rule.operator === 'contains') {
          return fieldValue !== undefined && fieldValue !== null
            && String(fieldValue).includes(String(conditionValue))
        }
        if (rule.operator === 'greater_than')
          return Number(fieldValue) > Number(conditionValue)
        if (rule.operator === 'less_than')
          return Number(fieldValue) < Number(conditionValue)

        return true
      }

      const result: Array<{ participantIndex: number, participantName: string, missingFields: string[] }> = []

      // Get unique participants (excluding linked ones), then deduplicate by RRN
      // so day-ticket slots (one entry per day for the same person) are only checked once.
      const seenRrns = new Set<string>()

      const uniqueParticipants = state.participants.filter(p => {
        if (p.linked_to_participant_id)
          return false
        const rrn = p.rijksregisternummer?.replace(/\D/g, '') || ''
        if (rrn.length === 11) {
          if (seenRrns.has(rrn))
            return false
          seenRrns.add(rrn)
        }

        return true
      })

      uniqueParticipants.forEach(participant => {
        const missingFields: string[] = []
        const participantIndex = state.participants.indexOf(participant)

        const participantName = participant.first_name && participant.last_name
          ? `${participant.first_name} ${participant.last_name}`
          : `Ticket ${participantIndex + 1}`

        state.camp_forms.forEach(form => {
          form.questions.forEach(question => {
            // Skip non-input questions
            if (['title', 'paragraph', 'link', 'image', 'separator'].includes(question.questionType))
              return

            if (isBaseFieldQuestion(question.label))
              return

            const conditionalRule = question.conditionalRule as {
              triggerQuestionId: string
              operator: 'equals' | 'not_equals' | 'contains' | 'greater_than' | 'less_than'
              value: string
              action: 'show' | 'hide' | 'require'
            } | null

            if (conditionalRule) {
              const conditionMet = isConditionMet(conditionalRule, participant)

              if (conditionalRule.action === 'show' && !conditionMet)
                return
              if (conditionalRule.action === 'hide' && conditionMet)
                return
            }

            // Check both camelCase and snake_case for compatibility
            const questionIsRequired = question.isRequired
              || (question as unknown as { is_required?: boolean }).is_required

            const requiresByCondition = conditionalRule && conditionalRule.action === 'require'
              ? isConditionMet(conditionalRule, participant)
              : false

            const isRequired = questionIsRequired || requiresByCondition

            if (!isRequired)
              return

            // Find the answer for this question
            const response = participant.form_responses?.find(
              r => r.question_id === question.id,
            )

            const answer = response?.answer

            // Check if answer has a meaningful value
            const hasValue = answer !== undefined
              && answer !== null
              && answer !== ''
              && (!Array.isArray(answer) || answer.length > 0)

            if (!hasValue) {
              missingFields.push(question.label || 'Onbekend veld')
            }
          })
        })

        if (missingFields.length > 0) {
          result.push({
            participantIndex,
            participantName,
            missingFields,
          })
        }
      })

      return result
    },
  },

  actions: {
    /**
     * Initialize cart for a specific camp
     */
    initCart(
      campId: string,
      campName: string,
      campDetails?: {
        location?: string
        age_min?: number
        age_max?: number
        start_date?: string
        end_date?: string
        period?: string
        refund_policy?: string
        enable_invoice?: boolean
        payment_methods?: string[]
      },
    ) {
      this.camp_id = campId
      this.camp_name = campName
      this.camp_location = campDetails?.location ?? null
      this.camp_age_min = campDetails?.age_min ?? null
      this.camp_age_max = campDetails?.age_max ?? null
      this.camp_start_date = campDetails?.start_date ?? null
      this.camp_end_date = campDetails?.end_date ?? null
      this.camp_period = campDetails?.period ?? null
      this.camp_refund_policy = campDetails?.refund_policy ?? null
      this.camp_enable_invoice = campDetails?.enable_invoice ?? null
      this.camp_payment_methods = campDetails?.payment_methods ?? null
      this.items = []
      this.participants = []
      this.coupon_code = null
      this.discount_amount = 0
      this.discount_details = []
      this.notes = ''
      this.step = 1
      this.camp_forms = []
      this.camp_forms_loaded = false
    },

    /**
     * Load forms linked to the current camp
     */
    async loadCampForms() {
      if (!this.camp_id) {
        return
      }

      // Always reload forms from API to get fresh data with correct isRequired values
      try {
        const response = await $api<{
          success: boolean
          data: { forms: CampForm[] }
        }>(`/api/camps/${this.camp_id}/forms`)

        if (response.success && response.data) {
          // Use type assertion to break deep type instantiation chain
          this.camp_forms = (response.data.forms || []) as CampForm[]
        }

        this.camp_forms_loaded = true
      }
      catch (error) {
        console.error('[CartStore] Error loading forms:', error)
        logApiError(`/api/camps/${this.camp_id}/forms`, error, {
          campId: this.camp_id,
        })
        this.camp_forms = []
        this.camp_forms_loaded = true
      }
    },

    /**
     * Add or update ticket item in cart
     */
    addTicketItem(item: CartTicketItem) {
      // Ensure day ticket quantity matches selected day tickets
      if (item.ticket_type === 'day' && item.day_tickets && item.day_tickets.length > 0) {
        item.quantity = item.day_tickets.reduce((sum, day) => sum + (day.quantity || 0), 0)
      }

      const existingIndex = this.items.findIndex(
        i => i.ticket_type_id === item.ticket_type_id,
      )

      if (existingIndex >= 0) {
        // Update existing item
        this.items[existingIndex] = item
      }
      else {
        // Add new item
        this.items.push(item)
      }

      // Ensure we have the right number of participant slots
      this.adjustParticipantSlots()
    },

    /**
     * Validate availability for ticket items against current camp capacity
     */
    async validateAvailability(items?: AvailabilityCheckItem[]): Promise<{ ok: boolean, message?: string }> {
      if (!this.camp_id) {
        return { ok: false, message: 'Geen kamp geselecteerd.' }
      }

      // Invite-based bookings bypass all availability checks — capacity is handled server-side
      if (this.invite_token) {
        return { ok: true }
      }

      const itemsToCheck = items ?? this.items

      try {
        const response = await $fetch<{ success: boolean, data: { weekTicketAvailability?: { ticket_type_id: string, available: number }[], dayAvailability?: { date: string, available: number }[] } }>(`/api/camps/${this.camp_id}/availability`)

        if (!response?.success) {
          return { ok: false, message: 'Beschikbaarheid kon niet worden gecontroleerd.' }
        }

        const weekAvailability = new Map(
          (response.data.weekTicketAvailability || []).map(w => [w.ticket_type_id, w.available]),
        )

        const dayAvailability = new Map(
          (response.data.dayAvailability || []).map(d => [d.date, d.available]),
        )

        for (const item of itemsToCheck) {
          if (item.ticket_type === 'week') {
            const available = weekAvailability.get(item.ticket_type_id)
            if (available !== undefined && item.quantity > available) {
              return { ok: false, message: 'Er zijn niet genoeg week tickets beschikbaar.' }
            }
          }

          if (item.ticket_type === 'day') {
            if (!item.day_tickets || item.day_tickets.length === 0) {
              return { ok: false, message: 'Selecteer minstens één dag voor dag tickets.' }
            }

            for (const day of item.day_tickets) {
              const available = dayAvailability.get(day.date)

              // Only reject if availability is known AND quantity exceeds it
              // Undefined availability means no capacity limit or data not loaded
              if (available !== undefined && day.quantity > available) {
                return { ok: false, message: `Er zijn niet genoeg dag tickets beschikbaar voor ${day.date}.` }
              }
            }
          }
        }

        return { ok: true }
      }
      catch (error) {
        logApiError(`/api/camps/${this.camp_id}/availability`, error, { items: itemsToCheck })

        return { ok: false, message: 'Beschikbaarheid kon niet worden gecontroleerd.' }
      }
    },

    /**
     * Remove ticket item from cart
     */
    removeTicketItem(ticketTypeId: string) {
      const index = this.items.findIndex(
        i => i.ticket_type_id === ticketTypeId,
      )

      if (index >= 0) {
        this.items.splice(index, 1)
        this.adjustParticipantSlots()
      }
    },

    /**
     * Update ticket quantity
     */
    updateTicketQuantity(ticketTypeId: string, quantity: number) {
      const item = this.items.find(i => i.ticket_type_id === ticketTypeId)
      if (item) {
        item.quantity = Math.max(0, quantity)
        if (item.quantity === 0)
          this.removeTicketItem(ticketTypeId)
        else this.adjustParticipantSlots()
      }
    },

    /**
     * Adjust participant slots based on ticket quantities
     */
    adjustParticipantSlots() {
      const requiredSlots = this.requiredParticipants

      // Remove excess participants
      if (this.participants.length > requiredSlots)
        this.participants = this.participants.slice(0, requiredSlots)

      // Add empty participant slots if needed
      while (this.participants.length < requiredSlots) {
        this.participants.push({
          tempId: `temp-${Date.now()}-${Math.random()}`,
          first_name: '',
          last_name: '',
          date_of_birth: '',
        })
      }
    },

    /**
     * Update participant information
     */
    updateParticipant(index: number, data: Partial<CartParticipant>) {
      if (index >= 0 && index < this.participants.length) {
        const existing = this.participants[index]
        if (!existing)
          return

        this.participants[index] = {
          ...existing,
          ...data,
        }
      }
    },

    /**
     * Update form response for a participant
     */
    updateParticipantFormResponse(
      participantIndex: number,
      questionId: string,
      answer: FormAnswer,
    ) {
      if (participantIndex < 0 || participantIndex >= this.participants.length)
        return

      const participant = this.participants[participantIndex]
      if (!participant)
        return

      if (!participant.form_responses)
        participant.form_responses = []

      const existingIndex = participant.form_responses.findIndex(
        r => r.question_id === questionId,
      )

      if (existingIndex >= 0 && participant.form_responses[existingIndex])
        participant.form_responses[existingIndex].answer = answer
      else participant.form_responses.push({ question_id: questionId, answer })
    },

    /**
     * Set all form responses for a participant at once
     */
    setParticipantFormResponses(
      participantIndex: number,
      responses: { question_id: string, answer: FormAnswer }[],
    ) {
      if (
        participantIndex >= 0
        && participantIndex < this.participants.length
      ) {
        const participant = this.participants[participantIndex]
        if (participant)
          participant.form_responses = responses
      }
    },

    /**
     * Link a participant to another (for deduplication - same attendee, multiple tickets)
     * The linked participant will share form data with the source participant
     */
    linkParticipant(participantIndex: number, sourceParticipantId: string) {
      if (
        participantIndex >= 0
        && participantIndex < this.participants.length
      ) {
        const participant = this.participants[participantIndex]
        if (!participant)
          return

        const sourceParticipant = this.participants.find(
          p => p.tempId === sourceParticipantId,
        )

        if (sourceParticipant) {
          // Copy basic info from source
          participant.linked_to_participant_id = sourceParticipantId
          participant.first_name = sourceParticipant.first_name
          participant.last_name = sourceParticipant.last_name
          participant.date_of_birth = sourceParticipant.date_of_birth
          participant.rijksregisternummer
            = sourceParticipant.rijksregisternummer
          participant.gender = sourceParticipant.gender
          participant.medical_info = sourceParticipant.medical_info
          participant.allergies = sourceParticipant.allergies
          participant.emergency_contact_name
            = sourceParticipant.emergency_contact_name
          participant.emergency_contact_phone
            = sourceParticipant.emergency_contact_phone

          // Deep clone form responses to avoid shared reference issues
          participant.form_responses = JSON.parse(
            JSON.stringify(sourceParticipant.form_responses || {}),
          )
        }
      }
    },

    /**
     * Unlink a participant (make them independent again)
     */
    unlinkParticipant(participantIndex: number) {
      if (
        participantIndex >= 0
        && participantIndex < this.participants.length
      ) {
        const participant = this.participants[participantIndex]
        if (participant)
          participant.linked_to_participant_id = undefined
      }
    },

    /**
     * Find existing participant by rijksregisternummer or name+birthday
     * Returns the participant if found, null otherwise
     */
    findMatchingParticipant(
      data: {
        rijksregisternummer?: string
        first_name?: string
        last_name?: string
        date_of_birth?: string
      },
      excludeTempId?: string,
    ): CartParticipant | null {
      // First try to match by rijksregisternummer (most reliable)
      if (data.rijksregisternummer) {
        const match = this.participants.find(
          p =>
            p.tempId !== excludeTempId
            && p.rijksregisternummer
            && p.rijksregisternummer.replace(/\D/g, '')
            === data.rijksregisternummer?.replace(/\D/g, ''),
        )

        if (match)
          return match
      }

      // Fall back to name + date_of_birth match
      if (data.first_name && data.last_name && data.date_of_birth) {
        const match = this.participants.find(
          p =>
            p.tempId !== excludeTempId
            && !p.linked_to_participant_id // Don't match linked participants
            && p.first_name?.toLowerCase() === data.first_name?.toLowerCase()
            && p.last_name?.toLowerCase() === data.last_name?.toLowerCase()
            && p.date_of_birth === data.date_of_birth,
        )

        if (match)
          return match
      }

      return null
    },

    /**
     * Auto-detect and suggest participant links based on rijksregisternummer or name+birthday
     * Returns array of suggested links: { participantIndex, suggestedSourceId }
     */
    detectDuplicateParticipants(): {
      participantIndex: number
      suggestedSourceId: string
    }[] {
      const suggestions: {
        participantIndex: number
        suggestedSourceId: string
      }[] = []

      const processed = new Set<string>()

      this.participants.forEach((participant, index) => {
        if (
          processed.has(participant.tempId)
          || participant.linked_to_participant_id
        ) {
          return
        }

        processed.add(participant.tempId)

        // Look for other participants that match this one
        this.participants.forEach((other, otherIndex) => {
          if (
            otherIndex <= index
            || processed.has(other.tempId)
            || other.linked_to_participant_id
          ) {
            return
          }

          // Check for match
          const isMatch = this.findMatchingParticipant(
            {
              rijksregisternummer: other.rijksregisternummer,
              first_name: other.first_name,
              last_name: other.last_name,
              date_of_birth: other.date_of_birth,
            },
            other.tempId,
          )

          if (isMatch && isMatch.tempId === participant.tempId) {
            suggestions.push({
              participantIndex: otherIndex,
              suggestedSourceId: participant.tempId,
            })
            processed.add(other.tempId)
          }
        })
      })

      return suggestions
    },

    /**
     * Assign ticket to participant
     */
    assignTicketToParticipant(
      participantIndex: number,
      assignment: TicketAssignment,
    ) {
      if (
        participantIndex >= 0
        && participantIndex < this.participants.length
      ) {
        const participant = this.participants[participantIndex]
        if (!participant)
          return

        if (!participant.ticket_assignments)
          participant.ticket_assignments = []

        participant.ticket_assignments.push(assignment)
      }
    },

    /**
     * Clear ticket assignments for a participant
     */
    clearTicketAssignments(participantIndex: number) {
      if (
        participantIndex >= 0
        && participantIndex < this.participants.length
      ) {
        const participant = this.participants[participantIndex]
        if (participant)
          participant.ticket_assignments = []
      }
    },

    /**
     * Apply coupon code
     */
    async applyCoupon(code: string) {
      const { validateCoupon } = useBookings()

      if (!this.camp_id)
        throw new Error('No camp selected')

      try {
        const response = await validateCoupon(code, this.camp_id)
        if (response.success && response.data) {
          this.coupon_code = code
          await this.recalculateTotal()

          return response
        }
        throw new Error(response.message || 'Invalid coupon code')
      }
      catch (error) {
        this.coupon_code = null
        throw error
      }
    },

    /**
     * Remove coupon code
     */
    async removeCoupon() {
      this.coupon_code = null
      await this.recalculateTotal()
    },

    /**
     * Recalculate cart total with discounts
     */
    async recalculateTotal() {
      if (!this.camp_id || this.items.length === 0) {
        this.discount_amount = 0
        this.discount_details = []

        return
      }

      const { calculateTotal } = useBookings()

      try {
        const response = await calculateTotal({
          camp_id: this.camp_id,
          ticket_items: this.items.map(item => ({
            ticket_type_id: item.ticket_type_id,
            quantity: item.quantity,
            day_tickets: item.day_tickets,
          })),
          coupon_code: this.coupon_code || undefined,
        })

        if (response.success && response.data) {
          this.discount_amount = response.data.discount_amount || 0
          this.discount_details = response.data.discount_details || []
        }
      }
      catch (error) {
        console.error('Error calculating total:', error)
      }
    },

    /**
     * Set checkout step
     */
    setStep(step: number) {
      this.step = Math.max(1, Math.min(4, step))
    },

    /**
     * Go to next step
     */
    nextStep() {
      this.setStep(this.step + 1)
    },

    /**
     * Go to previous step
     */
    previousStep() {
      this.setStep(this.step - 1)
    },

    /**
     * Clear entire cart
     */
    clearCart() {
      this.camp_id = null
      this.camp_name = null
      this.camp_location = null
      this.items = []
      this.participants = []
      this.coupon_code = null
      this.discount_amount = 0
      this.discount_details = []
      this.notes = ''
      this.step = 1
      this.invoice_required = false
      this.invoice_company_name = ''
      this.invoice_vat_number = ''
      this.invoice_address = ''
      this.invoice_country = ''
      this.camp_forms = []
      this.camp_forms_loaded = false
      this.invite_token = null
      this.invite_free_choice = false

      // Clean up invite token from browser storages
      if (import.meta.client) {
        sessionStorage.removeItem('invite_token')
        localStorage.removeItem('invite_token')
      }
    },

    /**
     * Prepare booking payload for API submission
     * For linked participants, copy form_responses from source participant
     */
    prepareBookingPayload() {
      if (!this.camp_id)
        throw new Error('No camp selected')

      // Build a list of ticket_type_ids to assign to participants (in order)
      // This creates a flat array where each ticket type appears quantity times
      const ticketTypeQueue: string[] = []
      for (const item of this.items) {
        for (let i = 0; i < item.quantity; i++) {
          ticketTypeQueue.push(item.ticket_type_id)
        }
      }

      // Resolve form_responses for linked participants
      const resolvedParticipants = this.participants.map((p, index) => {
        let formResponses = p.form_responses || []

        // If this participant is linked to another, get form responses from source
        if (p.linked_to_participant_id) {
          const sourceParticipant = this.participants.find(
            sp => sp.tempId === p.linked_to_participant_id,
          )

          if (sourceParticipant)
            formResponses = sourceParticipant.form_responses || []
        }

        // Transform form_responses to match RPC function expectations
        // RPC expects: [{ form_id: UUID, answers: [{ question_id: UUID, answer_value: FormAnswer }] }]
        // We have: [{ question_id: UUID, answer: FormAnswer }]
        const transformedFormResponses: Array<{
          form_id: string
          answers: Array<{ question_id: string, answer_value: FormAnswer }>
        }> = []

        if (
          formResponses.length > 0
          && this.camp_forms
          && this.camp_forms.length > 0
        ) {
          // Group responses by form_id
          const formResponsesMap = new Map<
            string,
            Array<{ question_id: string, answer_value: FormAnswer }>
          >()

          for (const response of formResponses) {
            // Find which form this question belongs to
            for (const campForm of this.camp_forms) {
              const question = campForm.questions?.find(
                q => q.id === response.question_id,
              )

              if (question) {
                const formId = campForm.id // CampForm uses 'id', not 'form_id'
                if (!formResponsesMap.has(formId)) {
                  formResponsesMap.set(formId, [])
                }
                formResponsesMap.get(formId)!.push({
                  question_id: response.question_id,
                  answer_value: response.answer,
                })
                break
              }
            }
          }

          // Convert map to array format expected by RPC
          for (const [form_id, answers] of formResponsesMap.entries()) {
            transformedFormResponses.push({ form_id, answers })
          }
        }
        else if (formResponses.length > 0) {
          console.warn(
            '⚠️ [Cart Store] Form responses exist but no camp_forms available!',
          )
        }

        // Get the ticket_type_id for this participant:
        // 1. First try from ticket_assignments (explicit assignment)
        // 2. Fall back to ticket queue (auto-assign based on order)
        const primaryTicketTypeId = p.ticket_assignments?.[0]?.ticket_type_id
          || ticketTypeQueue[index]
          || null

        return {
          first_name: p.first_name,
          last_name: p.last_name,
          date_of_birth: p.date_of_birth,
          rijksregisternummer: p.rijksregisternummer,
          gender: p.gender,
          medical_info: p.medical_info,
          allergies: p.allergies,
          emergency_contact_name: p.emergency_contact_name,
          emergency_contact_phone: p.emergency_contact_phone,
          ticket_type_id: primaryTicketTypeId,
          attendance_date: p.attendance_date,
          user_attendee_id: p.user_attendee_id,
          form_responses: transformedFormResponses,
        }
      })

      return {
        camp_id: this.camp_id,
        ticket_items: this.items.map(item => ({
          ticket_type_id: item.ticket_type_id,
          quantity: item.quantity,
          day_tickets: item.day_tickets,
        })),
        participants: resolvedParticipants,
        coupon_code: this.coupon_code || undefined,
        notes: this.notes || undefined,
        invite_token: this.invite_token || undefined,

        // Include invoice fields if invoice is required
        invoice_company_name: this.invoice_required
          ? this.invoice_company_name
          : undefined,
        invoice_vat_number: this.invoice_required
          ? this.invoice_vat_number
          : undefined,
        invoice_address: this.invoice_required
          ? this.invoice_address
          : undefined,
        invoice_country: this.invoice_required
          ? this.invoice_country
          : undefined,
      }
    },

    /**
     * Save cart state to localStorage
     */
    saveToLocalStorage() {
      if (process.client) {
        try {
          const state = {
            camp_id: this.camp_id,
            camp_name: this.camp_name,
            camp_location: this.camp_location,
            camp_age_min: this.camp_age_min,
            camp_age_max: this.camp_age_max,
            camp_start_date: this.camp_start_date,
            camp_end_date: this.camp_end_date,
            camp_period: this.camp_period,
            items: this.items,
            participants: this.participants,
            coupon_code: this.coupon_code,
            notes: this.notes,
            step: this.step,
            invoice_required: this.invoice_required,
            invoice_company_name: this.invoice_company_name,
            invoice_vat_number: this.invoice_vat_number,
            invoice_address: this.invoice_address,
            invoice_country: this.invoice_country,

            // Don't save camp_forms to localStorage - always fetch fresh from API
            timestamp: Date.now(),
          }

          localStorage.setItem('cart', JSON.stringify(state))
        }
        catch (error) {
          console.error('Error saving cart to localStorage:', error)
        }
      }
    },

    /**
     * Load cart state from localStorage
     */
    loadFromLocalStorage() {
      if (process.client) {
        try {
          const saved = localStorage.getItem('cart')
          if (saved) {
            const state = JSON.parse(saved)

            // Only load if less than 24 hours old
            const age = Date.now() - (state.timestamp || 0)
            if (age < 24 * 60 * 60 * 1000) {
              this.camp_id = state.camp_id
              this.camp_name = state.camp_name
              this.camp_location = state.camp_location || null
              this.camp_age_min = state.camp_age_min
              this.camp_age_max = state.camp_age_max
              this.camp_start_date = state.camp_start_date
              this.camp_end_date = state.camp_end_date
              this.camp_period = state.camp_period
              this.items = state.items || []
              this.participants = state.participants || []
              this.coupon_code = state.coupon_code
              this.notes = state.notes || ''
              this.step = state.step || 1
              this.invoice_required = state.invoice_required || false
              this.invoice_company_name = state.invoice_company_name || ''
              this.invoice_vat_number = state.invoice_vat_number || ''
              this.invoice_address = state.invoice_address || ''
              this.invoice_country = state.invoice_country || ''

              // Don't restore camp_forms from localStorage - always fetch fresh from API
              this.camp_forms = []
              this.camp_forms_loaded = false

              // Recalculate totals
              this.recalculateTotal()
            }
            else {
              // Clear expired cart
              localStorage.removeItem('cart')
            }
          }
        }
        catch (error) {
          console.error('Error loading cart from localStorage:', error)
        }
      }
    },
  },
})
